<template>
  <section
    v-if="features.length > 0"
    class="icon-group-section"
  >
    <b-container>
      <b-row class="justify-content-md-center">
        <b-col>
          <div
            class="icon-group"
            :class="`${featureGridStyle} ${selectedGridGap}`"
          >
            <div
              v-for="(feature, index) in features"
              :key="index"
              class="grid-item"
            >
              <template v-if="isFeatureLink(feature)">
                <component
                  :is="linkComponent(feature)"
                  :to="{ path: getFeatureButton(feature).url }"
                  :href="$options.filters.toUrl(getFeatureButton(feature).url)"
                  class="icon-cta-link"
                >
                  <div
                    class="icon-cta-item"
                    :class="`${selectedctaItemStyle} ${selectedctaItemDirection}`"
                  >
                    <div class="icon-cta-item-content">
                      <component
                        :is="getLayoutComponent"
                        :feature="feature"
                        :constructed="constructed"
                        v-bind="getLayoutProps"
                      />
                    </div>
                  </div>
                </component>
              </template>
              <template v-else>
                <div
                  class="icon-cta-item"
                  :class="`${selectedctaItemStyle} ${selectedctaItemDirection}`"
                >
                  <div class="icon-cta-item-content">
                    <component
                      :is="getLayoutComponent"
                      :feature="feature"
                      :constructed="constructed"
                      v-bind="getLayoutProps"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </section>
</template>

<script>
export default {
  name: 'sp-feature-feature',
  components: {
    'sp-feature-card': () => import('./feature-layouts/feature-card/sp-feature-card'),
    'sp-feature-quote-card': () => import('./feature-layouts/quote/sp-feature-quote'),
    'sp-feature-image-title-description-button': () => import('./feature-layouts/image-title-description-button'),
    'sp-feature-quote': () => import('./feature-layouts/quote')
  },
  props: {
    features: {
      type: Array,
      required: true
    },
    featuresStyle: {
      type: String,
      required: true
    },
    featuresLayout: {
      type: String,
      default: 'image-title-description-button'
    },
    layoutProps: {
      type: Object,
      default: () => ({})
    },
    constructed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedGridGap: 'grid-gap-1',
      selectedctaItemDirection: 'icon-cta-column',
      selectedctaItemStyle: 'icon-cta-ghost'
    }
  },
  computed: {
    getLayoutProps() {
      let props = this.layoutProps
      if (this.featuresLayout === 'card-text-overlay') {
        props.cardStyle = 'text-overlay'
      }

      return props
    },
    featureGridStyle() {
      switch (this.featuresStyle) {
      case '1col':
        return 'grid-1'

      case '2col':
        return 'grid-2'

      case '3col':
        return 'grid-3'

      case '4col':
        return 'grid-4'

      default:
        return 'grid-2'
      }
    },
    getLayoutComponent() {
      let layout = this.featuresLayout
      if (layout === 'card-text-overlay') {
        layout = 'card'
      }

      return `sp-feature-${layout}`
    }
  },
  methods: {
    linkComponent(feature) {
      if (/^#|http(s)?:\/\//.test(this.getFeatureButton(feature).url)) return 'a'
      return 'router-link'
    },
    isFeatureLink(feature) {
      const btn = this.getFeatureButton(feature)
      return btn && btn.url && !btn.title
    },
    getFeatureButton(feature) {
      return feature.button
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@scss/custom.scss";

.icon-group-section {
  padding: 3em 0 0;
}

.icon-group {
  display: grid;
  margin-bottom: 2.5em;

  &.no-gap {
    grid-gap: 0;
  }
  &.grid-gap-1 {
    grid-gap: 1.5em;
  }
  &.grid-gap-2 {
    grid-gap: 2em;
  }
  &.grid-2 {
    grid-template-columns: repeat(1, 1fr);

    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  &.grid-3 {
    grid-template-columns: repeat(1, 1fr);

    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(lg) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  &.grid-4 {
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(lg) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  &.grid-5 {
    grid-template-columns: repeat(2, 1fr);
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(xl) {
      grid-template-columns: repeat(5, 1fr);
    }
  }
}
.grid-item {
  overflow: hidden;
}

::v-deep .icon-cta-item {
  height: 100%;
  &.icon-cta-row {
    .icon-cta-item-content {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    }

    .icon-image {
      margin-right: 1em;
      display: block;
    }
  }
  &.icon-cta-column {
    text-align: center;
    justify-content: center;
    .icon-image {
      margin-bottom: 1em;
    }
    .action {
      justify-content: center;
    }
  }
  .icon-cta-item-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;
  }
  &.icon-cta-outlined,
  &.icon-cta-card {
    padding: 1.5em;
  }
  &.icon-cta-ghost,
  &.icon-cta-outlined,
  &.icon-cta-card {
    color: var(--main-text-color);
  }

  &.icon-cta-outlined {
    border: 1px solid;
    border-color: var(--main-border-color);
    border-radius: 3px;
    @include transition(color 0.3s, border-color 0.3s);
  }

  &.icon-cta-card {
    background-color: var(--main-element-background-color);
    @include transition(color 0.3s, background-color 0.3s);
  }

  a {
    color: var(--main-text-color);
    text-decoration: none;
  }

  .title {
    color: var(--main-text-heading-color);
  }

  .description {
    font-size: 0.9em;
    margin-bottom: 1rem;
  }
  .content {
    display: flex;
    flex-direction: column;
    word-break: break-word;
  }
}
.icon-cta-link {
  @include hover-focus {
    .icon-cta-card {
      background-color: var(--main-highlight-background-color);
      color: var(--main-highlight-text-color);
    }
  }
}

.icon-cta-link {
  @include hover-focus {
    text-decoration: none;
    .icon-cta-outlined {
      border-color: var(--main-border-color-highlight);
    }
  }
}
</style>
